import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import DocsSidebar from "@/components/app/DocsSidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight, Clock, Rss } from "lucide-react";

/**
 * /docs/changelog — a "What's new" timeline that auto-updates whenever
 * something is published to the internal /blog. Static dates server-side
 * (post.published_at), grouped by month, fully crawlable.
 */
function monthKey(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long" });
}
function dayLabel(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function DocsChangelog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "What's new — ArloSEO";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Every ArloSEO update, journal post, and shipping moment — in chronological order.",
      );
    }
    (async () => {
      try {
        const { data } = await api.get("/blog/posts?limit=100");
        setPosts(data.posts || []);
      } catch {
        setPosts([]);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    if (!posts) return null;
    const groups = new Map();
    for (const p of posts) {
      const k = monthKey(p.published_at);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(p);
    }
    return Array.from(groups.entries()); // preserves recency order from the API
  }, [posts]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-20" data-testid="docs-changelog-page">
        <Link
          to="/docs"
          className="inline-flex items-center gap-1.5 text-xs font-mono-data uppercase tracking-widest text-muted-foreground hover:text-foreground"
          data-testid="docs-changelog-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All docs
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-[18rem,1fr] gap-12">
          <DocsSidebar activeSlug="changelog" />

          <article className="min-w-0">
            <div className="flex items-center gap-2 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
              <Rss className="w-3 h-3" /> auto-generated from /blog
            </div>
            <h1 className="font-semibold tracking-tight mt-4 text-4xl sm:text-5xl leading-[1.05]">
              What&apos;s{" "}
              <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
                new
              </span>{" "}
              at ArloSEO.
            </h1>
            <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Every shipping moment, journal entry, and product note — in the order Arlo wrote them.
            </p>

            <hr className="my-10 border-border" />

            {/* States */}
            {posts === null && (
              <div className="text-sm text-muted-foreground" data-testid="docs-changelog-loading">
                Loading the timeline…
              </div>
            )}
            {posts && posts.length === 0 && (
              <div className="border border-dashed border-foreground/30 p-12 text-center" data-testid="docs-changelog-empty">
                <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
                  <OtterIcon size={36} />
                </div>
                <h3 className="font-display tracking-tight text-2xl">Nothing shipped yet.</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Arlo&apos;s drafting his first post — this page lights up automatically the moment he publishes.
                </p>
              </div>
            )}

            {/* Timeline */}
            {grouped && grouped.length > 0 && (
              <ol className="relative border-l border-border ml-2" data-testid="docs-changelog-timeline">
                {grouped.map(([month, items], gi) => (
                  <li key={month} className="mb-12" data-testid={`docs-changelog-month-${gi}`}>
                    <div className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 bg-foreground" />
                    <div className="ml-6">
                      <div className="font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                        {month}
                      </div>
                      <ul className="mt-4 space-y-4">
                        {items.map((p, i) => (
                          <li key={p.id} data-testid={`docs-changelog-entry-${p.slug}`}>
                            <Link
                              to={`/blog/${p.slug}`}
                              className="group block border border-border bg-white p-5 hover:border-foreground transition-colors"
                            >
                              <div className="flex flex-wrap items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                                <span>{dayLabel(p.published_at)}</span>
                                {p.target_keyword && (
                                  <span className="border border-border bg-secondary/50 px-1.5 py-0.5">
                                    {p.target_keyword}
                                  </span>
                                )}
                                <span className="inline-flex items-center gap-1">
                                  <Clock className="w-3 h-3" /> {p.reading_minutes} min
                                </span>
                                <span className="ml-auto inline-flex items-center gap-1 text-foreground/70">
                                  <Rss className="w-3 h-3" /> journal
                                </span>
                              </div>
                              <h3 className="font-semibold tracking-tight mt-2 text-lg group-hover:underline underline-offset-4 decoration-2">
                                {p.title}
                              </h3>
                              {p.excerpt && (
                                <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">
                                  {p.excerpt}
                                </p>
                              )}
                              <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium">
                                Read entry
                                <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                              </span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {/* CTA */}
            <div className="mt-16 border border-foreground bg-foreground text-background p-8 relative overflow-hidden" data-testid="docs-changelog-cta">
              <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
              <div className="relative">
                <span className="font-mono-data text-[10px] uppercase tracking-widest text-background/60">
                  // never miss a release
                </span>
                <h3 className="font-semibold tracking-tight text-2xl sm:text-3xl mt-3 leading-tight">
                  Start free and get every update.
                </h3>
                <p className="mt-3 text-background/70 max-w-xl text-sm">
                  Arlo emails Pro accounts a weekly digest with every shipped feature and journal entry — auto-summarized.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Button asChild className="rounded-none bg-background text-foreground hover:bg-background/90">
                    <Link to="/register" data-testid="docs-changelog-cta-signup">
                      Start free <ArrowRight className="w-4 h-4 ml-1.5" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="rounded-none border-background text-background hover:bg-background hover:text-foreground">
                    <Link to="/blog" data-testid="docs-changelog-cta-blog">Read the journal</Link>
                  </Button>
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
    </div>
  );
}
